import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';
import UplotReact from 'uplot-react';

import useRenderValuesInPreferredUnit from '@frolight/hooks/useRenderValuesInPreferredUnit';
import useResizeObserver from '@frolight/hooks/useResizeObserver';
import useUserPreferences from '@frolight/hooks/useUserPreferences';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';

import useFormattedData from './hooks/useFormattedData'; // Path to the hook you created

const OFFLINE_TIMEOUT = 11 * 60; // If a device hasn't sent data for 11 minutes, we consider it to be offlined

const TemperatureChart = ({ chartData }) => {
  const { lftext } = useLocalizedFormattedText();
  const { userPreferences } = useUserPreferences();
  const { renderTemperature } = useRenderValuesInPreferredUnit();
  const containerRef = useRef(null);
  const targetRef = useRef(null);
  const chartRef = useRef(null);

  const dimensions = useResizeObserver(containerRef);
  const formattedData = useFormattedData(chartData);
  
  let temperatureUnitShortNotation;
  if (userPreferences.temperatureUnit === 'celsius') {
    temperatureUnitShortNotation = '° C';
  } else if (userPreferences.temperatureUnit === 'fahrenheit') {
    temperatureUnitShortNotation = '° F';
  }

  const createGaps = (self, seriesId, idx0, idx1, conditionFn) => {
    const xData = self.data[0];
    const yData = self.data[seriesId];
    const gaps = [];

    for (let i = idx0 + 1; i <= idx1; i++) {
      if (!isNaN(yData[i]) && conditionFn(xData[i] - xData[i - 1])) {
        uPlot.addGap(
          gaps,
          Math.round(self.valToPos(xData[i - 1], 'x', true)),
          Math.round(self.valToPos(xData[i], 'x', true))
        );
      }
    }

    return gaps.toSorted((a, b) => a[0] - b[0]);
  };

  const createGapsWhenOffline = (self, seriesId, idx0, idx1) => {
    return createGaps(self, seriesId, idx0, idx1, delta => delta > OFFLINE_TIMEOUT);
  };

  const createGapsWhenOnline = (self, seriesId, idx0, idx1) => {
    return createGaps(self, seriesId, idx0, idx1, delta => delta <= OFFLINE_TIMEOUT);
  };

  const getAdjustedChartContainerHeight = (width) => {
    if (width <= 300) {
      return 260;
    } else if (width >= 1100) {
      return 100;
    } else if (width < 800) {
      // Linear interpolation from 500 (300) to 800 (200)
      const t = (width - 500) / (800 - 500); // t goes from 0 to 1
      return 180 - t * (180 - 120); // 180 to 120
    } else {
      // Linear interpolation from 800 (200) to 1000 (150)
      const t = (width - 800) / (1000 - 800); // t goes from 0 to 1
      return 140 - t * (140 - 120); // 140 to 120
    }
  };

  const options = useMemo(() => {
    if (!dimensions) {
      return null;
    }
    return {
      width: dimensions.width,
      height: dimensions.height - getAdjustedChartContainerHeight(dimensions.width),
      scales: {
        'contactor_status': { auto: false, range: [0, 1] },
        'overrule_status': { auto: false, range: [0, 20] },
        'connection_status': { auto: false, range: [0, 1] }
      },
      series: [
        {
          label: lftext('pages.device_detail.temperature_chart.date_time'),
          value: (self, rawValue) =>
            rawValue ? (new Date(rawValue * 1000)).toLocaleString() : null
        },
        {
          label: `${lftext('pages.device_detail.temperature_chart.current_temperature', { temperatureUnitShortNotation })}`,
          scale: 'temperature_celsius',
          value: (self, rawValue) =>
            (rawValue !== null) ? renderTemperature(parseFloat(rawValue).toFixed(2), 2) : null,
          gaps: createGapsWhenOffline,
          width: 1,
          stroke: 'rgba(26, 57, 39, 1)'
        },
        {
          label: `${lftext('pages.device_detail.temperature_chart.min_temperature', { temperatureUnitShortNotation })}`,
          scale: 'temperature_celsius',
          value: (self, rawValue) =>
            (rawValue !== null) ? renderTemperature(parseFloat(rawValue).toFixed(2), 2) : null,
          gaps: createGapsWhenOffline,
          width: 1,
          stroke: 'rgba(26, 57, 39, 0.50)',
          points: {
            show: false
          }
        },
        {
          label: `${lftext('pages.device_detail.temperature_chart.max_temperature', { temperatureUnitShortNotation })}`,
          scale: 'temperature_celsius',
          value: (self, rawValue) =>
            (rawValue !== null) ? renderTemperature(parseFloat(rawValue).toFixed(2), 2) : null,
          gaps: createGapsWhenOffline,
          width: 1,
          stroke: 'rgba(26, 57, 39, 0.50)',
          points: {
            show: false
          }
        },
        {
          label: lftext('pages.device_detail.temperature_chart.contactor_status'),
          scale: 'contactor_status',
          value: (self, rawValue) =>
            (rawValue !== null) ? ((parseFloat(rawValue) === 1) ? lftext('pages.device_detail.temperature_chart.on') : lftext('pages.device_detail.temperature_chart.off')) : lftext('pages.device_detail.temperature_chart.off'),
          gaps: createGapsWhenOffline,
          fill: 'rgba(84, 166, 116, 0.20)',
          width: 1,
          paths: uPlot.paths.stepped({
            align: 1
          }),
          points: {
            show: false
          }
        },
        {
          label: lftext('pages.device_detail.temperature_chart.manual_overrule'),
          scale: 'overrule_status',
          value: (self, rawValue) =>
            (rawValue !== null) ? ((parseFloat(rawValue) === 1) ? lftext('pages.device_detail.temperature_chart.on') : lftext('pages.device_detail.temperature_chart.off')) : lftext('pages.device_detail.temperature_chart.off'),
          gaps: createGapsWhenOffline,
          fill: 'rgba(255, 170, 29, 0.6)',
          width: 1,
          paths: uPlot.paths.stepped({
            align: 1
          }),
          points: {
            show: false
          }
        },
        {
          label: `${lftext('pages.device_detail.temperature_chart.low_threshhold_temperature', { temperatureUnitShortNotation })}`,
          scale: 'temperature_celsius',
          value: (self, rawValue) =>
            (rawValue !== null) ? renderTemperature(parseFloat(rawValue).toFixed(2), 2) : null,
          gaps: createGapsWhenOffline,
          stroke: 'rgba(255, 0, 0, 1)',
          paths: uPlot.paths.stepped({
            align: 1
          }),
          width: 2,
          dash: [8, 8],
          points: {
            show: false
          }
        },
        {
          label: `${lftext('pages.device_detail.temperature_chart.high_threshhold_temperature',{ temperatureUnitShortNotation})}`,
          scale: 'temperature_celsius',
          value: (self, rawValue) =>
            (rawValue !== null) ? renderTemperature(parseFloat(rawValue).toFixed(2), 2) : null,
          gaps: createGapsWhenOffline,
          stroke: 'rgba(0, 0, 255, 1)',
          paths: uPlot.paths.stepped({
            align: 1
          }),
          width: 2,
          dash: [8, 8],
          points: {
            show: false
          }
        },
        {
          label: lftext('pages.device_detail.temperature_chart.connected'),
          scale: 'connection_status',
          value: (self, rawValue) =>
            rawValue && rawValue === 1 ? lftext('pages.device_detail.temperature_chart.connected') : lftext('pages.device_detail.temperature_chart.not_connected'),
          fill: 'rgba(255, 0, 0, 0.05)',
          width: 1,
          gaps: createGapsWhenOnline,
          paths: uPlot.paths.stepped({
            align: 1
          }),
          points: {
            show: false
          }
        }
      ],
      bands: [
        { series: [2, 1], fill: 'rgba(26, 57, 39, 0.20)', dir: 1 },
        { series: [1, 3], fill: 'rgba(26, 57, 39, 0.20)', dir: 1 }
      ],
      axes: [
        {
          values: (self, ticks) =>
            ticks.map(rawValue => (new Date(rawValue * 1000)).toLocaleDateString()),
          space: (self, axisIdx, scaleMin, scaleMax, plotDim) => {
            let rangeSecs = scaleMax - scaleMin;
            let rangeDays = rangeSecs / 86400;
            let pxPerDay = plotDim / rangeDays;
            return pxPerDay;
          }
        },
        {
          scale: 'temperature_celsius',
          label: `${lftext('pages.device_detail.temperature_chart.temperature', { temperatureUnitShortNotation })}`,
          values: (self, ticks) =>
            ticks.map(rawValue => renderTemperature(parseFloat(rawValue), 2))
        },
        {
          scale: 'contactor_status',
          label: lftext('pages.device_detail.temperature_chart.contactor_status'),
          values: (self, ticks) =>
            ticks.map(rawValue => rawValue === 1 ? `On` : `Off`),
          side: 1,
          grid: { show: false },
          ticks: { show: false },
          incrs: [1]
        }
      ]
    };
  }, [ dimensions, lftext, renderTemperature, temperatureUnitShortNotation, getAdjustedChartContainerHeight ]);

  // Directly call uPlot's setSize on window resize
  useEffect(() => {
    // Handler similar to the uPlot example:
    const handleResize = () => {
      if (chartRef.current && dimensions) {
        chartRef.current.setSize({
          width: dimensions.width,
          height: dimensions.height - getAdjustedChartContainerHeight(dimensions.width)
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [ dimensions, getAdjustedChartContainerHeight ]);

  // Capture the uPlot instance when it's created. Depending on the UplotReact wrapper,
  // you might be able to use an `onCreate` callback (or ref forwarding) to do this.
  const onChartCreated = (uInstance) => {
    chartRef.current = uInstance;
  };

  return (
    <div className="temperature_chart" ref={ containerRef }>
      <div className="temperature_chart__chart">
        <div className="temperature_chart__canvas" ref={ targetRef }>
          {
            formattedData && options ? (
              <UplotReact
                options={ options }
                data={ formattedData }
                target={ targetRef.current }
                onCreate={ onChartCreated }
              />
            ) : (
              <p>
                {lftext('common.loading')}
              </p>
            )
          }
        </div>
      </div>
    </div>
  );

}

export default TemperatureChart;
